import React, { useCallback, useEffect, useState } from 'react';

import EsConfirmDelete from './EsConfirmDelete';
import { API_COLORSHEET } from './EsConstants';
import { esRead } from './EsFetch';
import { EsSelect } from './EsPrime';
import { useEsTheme } from './EsThemeContext';
import { useEsStrings } from './EsStrings';

/**
 * The colorsheets as one list to swap between, for the theme menu: picking one applies it
 * in one action -- this session's signed out, the account's own colors signed in, an
 * admin's draft -- the way the Colorsheet page's list does, and asks first only over an
 * unsaved draft, which is the one thing a swap loses. Hidden while there is no store.
 *
 * `reachable` is the menu's: its panel stays mounted to fade, so a closed one keeps this
 * out of the tab order.
 */
export default function EsSheetSwitch( { 'data-testid': dataTestId, reachable = true, size } ) {
    const t = useEsStrings();
    const theme = useEsTheme();
    const [ list, setList ] = useState( null );
    const [ asking, setAsking ] = useState( null );
    const [ busy, setBusy ] = useState( false );

    const load = useCallback( () => esRead( API_COLORSHEET )
        .then( ( pList ) => setList( pList ?? { available: false, sheets: [] } ) )
        .catch( () => setList( { available: false, sheets: [] } ) ), [] );

    // Asked again each time the menu opens: a sheet saved, renamed, deleted or made the
    // default on the Colors page has to be here by the next look, and the panel never unmounts.
    useEffect( () => { if ( reachable ) load(); }, [ reachable, load ] );

    if ( !list?.available ) return null;

    const isDefault = ( pId ) => Boolean( pId ) && pId === list.defaultId;
    const shown = theme.sheet?.id ?? list.defaultId ?? null;
    const options = list.sheets.map( ( pSheet ) => ( {
        value: pSheet.id,
        label: isDefault( pSheet.id ) ? t( 'common-sheet-switch-default', '{name} (default)', { name: pSheet.name } ) : pSheet.name,
    } ) );

    const use = ( pSheet ) => theme.pickSheet( pSheet, isDefault( pSheet.id ) )
        .then( () => setAsking( null ) )
        .catch( () => undefined )
        .finally( () => setBusy( false ) );

    const pick = ( pId ) => {
        if ( !pId || pId === shown ) return;
        setBusy( true );
        esRead( API_COLORSHEET + '/' + pId )
            .then( ( pSheet ) => {
                if ( theme.dirty ) {
                    setBusy( false );
                    setAsking( pSheet );
                }
                else use( pSheet );
            } )
            .catch( () => setBusy( false ) );
    };

    return (
        <div>
            <div className="mb-1 text-xs font-medium leading-4">{ t( 'common-sheet-switch', 'Colorsheet' ) }</div>
            <EsSelect data-testid={ dataTestId } aria-label={ t( 'common-sheet-switch', 'Colorsheet' ) } size={ size }
                      value={ shown } options={ options }
                      disabled={ busy || !reachable }
                      onChange={ ( pEvent ) => pick( pEvent.target.value ) } />
            <EsConfirmDelete data-testid={ dataTestId && `${ dataTestId }-asking` }
                             open={ Boolean( asking ) }
                             title={ t( 'common-sheet-switch-title', 'Swap colorsheet?' ) }
                             what={ asking?.name
                                 ? t( 'common-sheet-switch-what-named', 'Your unsaved changes will be replaced by {name}.', { name: asking.name } )
                                 : t( 'common-sheet-switch-what', 'Your unsaved changes will be replaced by the sheet.' ) }
                             confirm={ t( 'common-sheet-switch-confirm', 'Swap' ) } severity={ null }
                             busy={ busy }
                             onConfirm={ () => { setBusy( true ); use( asking ); } }
                             onCancel={ () => setAsking( null ) } />
        </div>
    );
}
